import { useQuery } from "@tanstack/react-query";

import { ENDPOINTS } from "@/api/application-api";
import { useUserToken } from "@/context/auth-context";
import { Report } from "@/types/evaluate";
import { handleResponse } from "@/utils/errorResponseHandlers";
import { authHeader } from "@/utils/headers";

export interface GetReportsForPromptResponse {
  success: boolean;
  message: string;
  reports: Report[];
  has_prev: boolean;
  has_next: boolean;
  prev_num: number | null;
  next_num: number | null;
  page: number;
  pages: number;
  per_page: number;
  total: number;
}

interface GetReportsForPromptParams {
  workspace_id: number;
  prompt_id?: number;
  page?: number;
  per_page?: number;
  is_blueprint?: boolean;
  report_id?: number;
  parent_report_id?: number;
  dataset_id?: number;
  prompt_label_id?: number;
  prompt_version_id?: number;
  start_time?: string;
  end_time?: string;
  include_datasets?: boolean;
  include_columns?: boolean;
}
export const useGetReportsByPromptId = (params: GetReportsForPromptParams) => {
  const {
    workspace_id,
    prompt_id,
    page = 1,
    per_page = 10,
    is_blueprint,
    report_id,
    parent_report_id,
    dataset_id,
    prompt_label_id,
    prompt_version_id,
    start_time,
    end_time,
    include_datasets,
    include_columns,
  } = params;
  const userToken = useUserToken();

  return useQuery<GetReportsForPromptResponse>({
    queryKey: [
      "reportsForPrompt",
      workspace_id,
      prompt_id,
      report_id,
      parent_report_id,
      dataset_id,
      page,
      per_page,
      is_blueprint,
      prompt_label_id,
      prompt_version_id,
      start_time,
      end_time,
      include_datasets,
      include_columns,
    ],
    queryFn: () => {
      const url = new URL(ENDPOINTS.get_reports(workspace_id));

      if (prompt_id) {
        url.searchParams.append("prompt_id", prompt_id.toString());
      }
      url.searchParams.append("page", page.toString());
      url.searchParams.append("per_page", per_page.toString());
      if (is_blueprint) {
        url.searchParams.append("is_blueprint", is_blueprint.toString());
      }
      if (report_id) {
        url.searchParams.append("report_id", report_id.toString());
      }
      if (dataset_id) {
        url.searchParams.append("dataset_id", dataset_id.toString());
      }
      if (parent_report_id) {
        url.searchParams.append("parent_report_id", parent_report_id.toString());
      }
      if (prompt_label_id) {
        url.searchParams.append("prompt_label_id", prompt_label_id.toString());
      }

      if (prompt_version_id) {
        url.searchParams.append("prompt_version_id", String(prompt_version_id));
      }
      if (start_time) {
        url.searchParams.append("start_time", start_time.toString());
      }
      if (end_time) {
        url.searchParams.append("end_time", end_time.toString());
      }
      if (include_datasets) {
        url.searchParams.append("include_datasets", include_datasets.toString());
      }
      if (include_columns) {
        url.searchParams.append("include_columns", String(include_columns));
      }

      return fetch(url.toString(), {
        method: "GET",
        headers: authHeader(userToken),
      }).then(handleResponse);
    },
    enabled: !!workspace_id,
  });
};
